import React, { createContext, useCallback, useContext, useMemo } from 'react';
import {
  WidgetHostContext,
  dashboardWidgetRegistry,
  tabsWidgetRegistry,
} from '@/core/widgets/export';
import {
  SidePanelUiContext,
  getDeepDiveApp,
  openDeepDiveApp,
  openDashboardWidgetFromDeepDive,
} from '@/core/deep-dive/export';

const SIDE_PANEL_HOST_ID = 'side-panel-host';

const SETTINGS_WIDGET_ID = 'settings';

export type SettingsTab = 'connection' | 'appearance' | 'calendar' | 'notifications';
export type NotificationsSubTab = 'subscriptions' | 'profile' | 'contacts';

type SettingsHandler = (tab?: SettingsTab, subtab?: NotificationsSubTab) => void;

let settingsHandler: SettingsHandler | null = null;

export function registerSettingsHandler(handler: SettingsHandler): () => void {
  settingsHandler = handler;
  return () => {
    if (settingsHandler === handler) settingsHandler = null;
  };
}

export interface OpenWidgetResult {
  ok: boolean;
  reason?: 'unsupported-host' | 'unknown-widget';
}

type WidgetKind = 'deep-dive' | 'dashboard' | 'settings' | 'unknown';

// Lives under widget-navigation (not widgets, not deep-dive) purely as an
// internal detail of openWidget's dispatch below — it doesn't need its own
// module since nothing else in the app needs to ask "what kind of widget is
// this". Deep-dive apps are a small, fixed set (~4), so checking that
// registry first is cheap.
function resolveWidgetKind(id: string): WidgetKind {
  if (id === SETTINGS_WIDGET_ID) return 'settings';
  if (getDeepDiveApp(id)) return 'deep-dive';
  if (dashboardWidgetRegistry.getById(id) || tabsWidgetRegistry.getById(id)) return 'dashboard';
  return 'unknown';
}

export interface WidgetNavigationApi {
  openWidget(id: string, path?: string): Promise<OpenWidgetResult>;
}

export const WidgetNavigationContext = createContext<WidgetNavigationApi>({
  async openWidget(id: string) {
    console.warn('useOpenWidget called with no WidgetNavigationProvider mounted', id);
    return { ok: false, reason: 'unsupported-host' };
  },
});

export function WidgetNavigationProvider({ children }: React.PropsWithChildren<{}>) {
  const host = useContext(WidgetHostContext);
  const sidePanelUi = useContext(SidePanelUiContext);

  const openWidget = useCallback(
    async (id: string, path?: string): Promise<OpenWidgetResult> => {
      const kind = resolveWidgetKind(id);
      if (kind === 'unknown') {
        console.warn(`openWidget: unknown widget id "${id}"`);
        return { ok: false, reason: 'unknown-widget' };
      }

      if (kind === 'settings') {
        const [tab, subtab] = (path ?? '').split('/');
        if (!settingsHandler) {
          console.warn('openWidget: no SettingsDrawerShell mounted');
          return { ok: false, reason: 'unsupported-host' };
        }
        settingsHandler(
          (tab || undefined) as SettingsTab | undefined,
          (subtab || undefined) as NotificationsSubTab | undefined,
        );
        return { ok: true };
      }

      const isSidePanelHost = host.hostId === SIDE_PANEL_HOST_ID;

      if (kind === 'deep-dive') {
        if (isSidePanelHost) {
          sidePanelUi.openApp(id, false);
          if (path) sidePanelUi.setWidgetPath(path);
        } else {
          await openDeepDiveApp(id, path);
        }
        return { ok: true };
      }

      // kind === 'dashboard'
      if (isSidePanelHost) {
        openDashboardWidgetFromDeepDive(id, path);
        // Get out of the way so the widget we just navigated to is visible —
        // only meaningful when inline; the popup case in
        // openDashboardWidgetFromDeepDive closes the window itself instead.
        sidePanelUi.closePanel('deep-dive');
        return { ok: true };
      }

      if (path) await host.setWidgetPath(id, path);
      else host.addWidgetById(id);
      return { ok: true };
    },
    [host, sidePanelUi],
  );

  const value = useMemo(() => ({ openWidget }), [openWidget]);

  return (
    <WidgetNavigationContext.Provider value={value}>{children}</WidgetNavigationContext.Provider>
  );
}

export default function useOpenWidget() {
  return useContext(WidgetNavigationContext).openWidget;
}
